/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Copy, Download, ScanLine, Calendar, MessageCircle, CheckCircle2, Users, AlertCircle, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Phase 323e — Live "Recent scans" feed.
 * Polls every 5s while the dialog is open AND a QR has been generated.
 * Shows the most recent N students who scanned + checked in, so the
 * coach can spot missing players at a glance.
 */
function RecentScansFeed({ kind, batch, date }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let timer = null;
    const poll = async () => {
      try {
        const res = await api.get(
          `/academy/attendance-qr/recent-scans?batch=${encodeURIComponent(batch)}&date=${date}&kind=${kind}&limit=15`
        );
        if (mountedRef.current) {
          setScans(res.data?.scans || []);
          setLoading(false);
        }
      } catch (err) {
        if (mountedRef.current) setLoading(false);
      } finally {
        if (mountedRef.current) timer = setTimeout(poll, 5000);
      }
    };
    poll();
    return () => { mountedRef.current = false; if (timer) clearTimeout(timer); };
  }, [batch, date, kind]);

  const formatAgo = (iso) => {
    if (!iso) return '';
    const secs = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
  };

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="attendance-qr-recent-scans">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> Recent scans
          <span className="text-emerald-600 font-normal">· live</span>
        </p>
        <span className="text-[10px] text-emerald-700 font-mono">{scans.length}</span>
      </div>
      {loading && scans.length === 0 && (
        <p className="text-xs text-emerald-700/70">Waiting for the first scan…</p>
      )}
      {!loading && scans.length === 0 && (
        <p className="text-xs text-emerald-700/70">Nobody has scanned yet. Share the QR to get started.</p>
      )}
      {scans.length > 0 && (
        <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
          {scans.map((s, i) => (
            <li key={`${s.custom_id}-${s.checked_in_at}-${i}`} className="flex items-center gap-2 text-xs" data-testid={`recent-scan-row-${i}`}>
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span className="font-medium text-emerald-900 truncate flex-1">{s.name}</span>
              {s.custom_id && (
                <span className="font-mono text-[10px] text-emerald-700">{s.custom_id}</span>
              )}
              <span className="text-emerald-600 text-[10px] shrink-0">{formatAgo(s.checked_in_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Phase 323f — Missing Players panel.
 * Diffs today's enrolled batch roster vs who has scanned. Surfaces each
 * unmarked student with a one-tap "Nudge on WhatsApp" link to the
 * guardian's phone. Only shown for kind='student' (employees don't have
 * a batch roster).
 */
function MissingPlayersPanel({ batch, date }) {
  const [data, setData] = useState({ missing: [], count: 0, roster_count: 0, marked_count: 0 });
  const [loading, setLoading] = useState(true);
  const [bulkNudging, setBulkNudging] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let timer = null;
    const poll = async () => {
      try {
        const res = await api.get(
          `/academy/attendance-qr/missing-players?batch=${encodeURIComponent(batch)}&date=${date}`
        );
        if (mountedRef.current) {
          setData(res.data || { missing: [], count: 0, roster_count: 0, marked_count: 0 });
          setLoading(false);
        }
      } catch (err) {
        if (mountedRef.current) setLoading(false);
      } finally {
        if (mountedRef.current) timer = setTimeout(poll, 8000);
      }
    };
    poll();
    return () => { mountedRef.current = false; if (timer) clearTimeout(timer); };
  }, [batch, date]);

  const handleNudge = (player) => {
    if (!player.guardian_phone) {
      toast.error('No phone on file for this player');
      return;
    }
    const digits = (player.guardian_phone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Saved phone is too short to dial');
      return;
    }
    const wa = digits.length === 10 ? '91' + digits : digits;
    const msg = `Hi! We missed ${player.name?.split(' ')[0] || 'your child'} at ${batch} practice today. Everything okay? Tap the link in our last message to mark attendance, or reply here.`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  const handleBulkNudge = async () => {
    if (bulkNudging || data.count === 0) return;
    const proceed = window.confirm(
      `Send a WhatsApp nudge to ${data.count} guardian${data.count > 1 ? 's' : ''}? This may take a few seconds.`
    );
    if (!proceed) return;
    setBulkNudging(true);
    try {
      const res = await api.post('/academy/attendance-qr/bulk-nudge-missing', { batch, date });
      const { sent = 0, failed = 0, skipped_no_phone = 0 } = res.data || {};
      const lines = [];
      if (sent) lines.push(`✅ ${sent} sent`);
      if (failed) lines.push(`⚠️ ${failed} failed (outside 24h window)`);
      if (skipped_no_phone) lines.push(`📵 ${skipped_no_phone} no phone`);
      toast.success(lines.join(' · ') || 'Bulk nudge complete', { duration: 8000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk nudge failed');
    } finally {
      setBulkNudging(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-800">
        Loading roster…
      </div>
    );
  }
  if (data.roster_count === 0) {
    return null; // No roster → silently hide (would just be noise).
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="attendance-qr-missing-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
          <AlertCircle className="h-3.5 w-3.5" /> Missing players
        </p>
        <div className="flex items-center gap-2">
          {data.count > 0 && (
            <button
              onClick={handleBulkNudge}
              disabled={bulkNudging}
              data-testid="missing-bulk-nudge"
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                bulkNudging
                  ? 'bg-amber-200 text-amber-600 cursor-wait'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
            >
              {bulkNudging ? 'Nudging…' : `Bulk Nudge (${data.count})`}
            </button>
          )}
          <span className="text-[10px] text-amber-700 font-mono">
            {data.marked_count}/{data.roster_count} marked
          </span>
        </div>
      </div>
      {data.count === 0 ? (
        <p className="text-xs text-amber-700/80 flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Everyone in {batch} batch is marked present. 🎉
        </p>
      ) : (
        <ul className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
          {data.missing.map((p, i) => (
            <li key={p.id} className="flex items-center gap-2 text-xs" data-testid={`missing-player-row-${i}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
              <span className="font-medium text-amber-900 truncate flex-1">{p.name}</span>
              {p.custom_id && <span className="font-mono text-[10px] text-amber-700">{p.custom_id}</span>}
              <button
                onClick={() => handleNudge(p)}
                disabled={!p.guardian_phone}
                data-testid={`missing-player-nudge-${i}`}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${
                  p.guardian_phone
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
                title={p.guardian_phone ? `Nudge ${p.name.split(' ')[0]} on WhatsApp` : 'No phone on file'}
              >
                <PhoneCall className="h-2.5 w-2.5" /> Nudge
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Phase 323 — Batch Attendance QR
 * --------------------------------
 * Owner/coach picks a batch + date. Server mints a signed token + QR.
 * Scanning the QR opens a public mobile-first scan landing page where
 * the student/employee enters their phone and the system auto-marks
 * them present in the matching batch.
 *
 * Props:
 *   open       — controlled flag
 *   onClose    — fn to close
 *   kind       — 'student' | 'employee'
 *   batches    — array of batch-name strings to choose from
 *                 (e.g. ['Morning','Afternoon','Evening','Weekend'])
 */
export default function AttendanceSessionQRDialog({ open, onClose, kind = 'student', batches = [] }) {
  const today = new Date().toISOString().slice(0, 10);
  const [batch, setBatch] = useState(batches[0] || 'Morning');
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Phase 323g — Auto-nudge T+15 toggle (students only).
  const [autoNudge, setAutoNudge] = useState(false);

  const handleGenerate = async () => {
    if (!batch) {
      toast.error('Please pick a batch');
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/academy/attendance-qr?batch=${encodeURIComponent(batch)}&date=${date}&kind=${kind}`);
      setData(res.data);
      // Schedule the auto-nudge in parallel (students only — employees don't have a batch roster).
      if (autoNudge && kind === 'student') {
        try {
          await api.post('/academy/attendance-qr/schedule-auto-nudge', { batch, date, fire_in_minutes: 15 });
          toast.success('Auto-nudge scheduled · fires in 15 min for any missing players');
        } catch (err) {
          toast.error('Auto-nudge could not be scheduled — generate the QR anyway and use Bulk Nudge manually.');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate attendance QR');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!data?.public_url) return;
    navigator.clipboard.writeText(data.public_url);
    toast.success('Public scan link copied!');
  };

  const handleDownload = () => {
    if (!data?.qr_data_url) return;
    const a = document.createElement('a');
    a.href = data.qr_data_url;
    a.download = `attendance-${kind}-${batch}-${date}.png`;
    a.click();
  };

  const handleShare = () => {
    if (!data?.public_url) return;
    const msg = `${kind === 'employee' ? 'Staff attendance' : 'Practice attendance'} for ${batch} on ${date}. Tap to check-in:\n${data.public_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  const reset = () => {
    setData(null);
    setBatch(batches[0] || 'Morning');
    setDate(today);
    setAutoNudge(false);
  };

  const title = kind === 'employee' ? 'Staff Attendance QR' : 'Batch Attendance QR';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose?.(); reset(); } }}>
      <DialogContent className="max-w-md" data-testid="attendance-qr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-emerald-600" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Print this for today's session. {kind === 'employee' ? 'Staff' : 'Students'} scan it on arrival → enter their phone → marked present automatically.
            QR expires in 24 hours.
          </DialogDescription>
        </DialogHeader>

        {!data && (
          <div className="space-y-3" data-testid="attendance-qr-form">
            <div className="space-y-1.5">
              <Label className="text-xs">Batch</Label>
              <Select value={batch} onValueChange={setBatch}>
                <SelectTrigger data-testid="attendance-qr-batch-select">
                  <SelectValue placeholder="Select batch" />
                </SelectTrigger>
                <SelectContent>
                  {(batches.length ? batches : ['Morning','Afternoon','Evening','Weekend']).map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Session Date
              </Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="attendance-qr-date-input"
              />
            </div>
            {/* Phase 323g — Auto-nudge T+15min toggle (students only) */}
            {kind === 'student' && (
              <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/50 p-2 cursor-pointer" data-testid="auto-nudge-toggle-wrap">
                <input
                  type="checkbox"
                  checked={autoNudge}
                  onChange={(e) => setAutoNudge(e.target.checked)}
                  data-testid="auto-nudge-toggle"
                  className="mt-0.5 h-4 w-4 accent-amber-600"
                />
                <span className="text-xs leading-snug text-amber-900">
                  <span className="font-semibold">Auto-nudge in 15 min</span>
                  <span className="block text-[10px] text-amber-700/80 mt-0.5">
                    If anyone is still missing 15 minutes after I generate the QR, send the WhatsApp nudge automatically.
                  </span>
                </span>
              </label>
            )}
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={handleGenerate}
              disabled={loading}
              data-testid="attendance-qr-generate-btn"
            >
              {loading ? 'Generating…' : <><ScanLine className="h-4 w-4 mr-1.5" /> Generate QR</>}
            </Button>
          </div>
        )}

        {loading && data === null && (
          <Skeleton className="h-64 w-64 mx-auto rounded-lg mt-3" />
        )}

        {data && (
          <div className="space-y-3" data-testid="attendance-qr-result">
            <div className="flex justify-center bg-white p-4 rounded-lg border border-slate-200">
              <img src={data.qr_data_url} alt="Attendance QR code" className="w-64 h-64" data-testid="attendance-qr-image" />
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs">
              <p className="font-semibold text-emerald-900">
                {data.batch} · {data.date}
              </p>
              <p className="text-emerald-700 mt-0.5">
                Expires {new Date(data.expires_at).toLocaleString()}
              </p>
              {autoNudge && kind === 'student' && (
                <p className="text-amber-700 mt-1 flex items-center gap-1" data-testid="auto-nudge-active-pill">
                  <AlertCircle className="h-3 w-3" /> Auto-nudge active · fires 15 min from now
                </p>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" size="sm" onClick={handleCopyLink} data-testid="attendance-qr-copy-link" className="text-xs">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload} data-testid="attendance-qr-download" className="text-xs">
                <Download className="h-3.5 w-3.5 mr-1" /> Download
              </Button>
              <Button size="sm" onClick={handleShare} data-testid="attendance-qr-share-wa" className="text-xs bg-emerald-600 hover:bg-emerald-700">
                <MessageCircle className="h-3.5 w-3.5 mr-1" /> Share
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={reset} className="w-full text-xs">
              Generate another
            </Button>
            {/* Phase 323d — Live recent scans feed */}
            <RecentScansFeed kind={kind} batch={data.batch} date={data.date} />
            {/* Phase 323f — Missing players panel (students only) */}
            {kind === 'student' && (
              <MissingPlayersPanel batch={data.batch} date={data.date} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
